import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import ReminderButton from "../common/ReminderButton";
import {
  renderSharedStatus,
  renderStatus,
  renderVeteransAffairsStatus,
} from "../../utils/columns";

dayjs.extend(utc);
dayjs.extend(relativeTime);

/**
 * Turns a row from each of the four activity endpoints into the shape
 * `ActivityCard` draws: a name, some detail lines, a status and optional
 * actions.
 *
 * This is where the wide tables' columns went (Matthew, 2026-07-30).
 * Nothing is dropped to make the narrower card fit -- the columns become
 * lines under the name instead, which is both less to read across and
 * gives long values room the old narrow strips never had. Status pills and
 * the reminder button are the same helpers the Home tables use, so a row
 * here and a row there say the same thing about the same record.
 *
 * What is left behind, and why:
 *
 *  - Reviews no longer show ASSIGNED ON. The one real field sacrificed:
 *    two dates on one line read as a range, and between "when it was
 *    shared" and "when it is due" only the latter is ever chased.
 *  - Assignments no longer offer "Link a draft" for an empty deliverable,
 *    and reviews no longer offer the reminder bell. Both are actions, not
 *    information, and both need state the dashboard does not have -- a
 *    modal and Home's notification plumbing respectively. An existing
 *    deliverable is still named, and `reviewRow` already takes an
 *    `onBellClick` for whenever the reminder is worth wiring up here.
 */

const DASH = "-";

const dateOnly = (value) => {
  if (!value) return null;
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : null;
};

const dateAndTime = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, HH:mm") : null;
};

// Joins the parts of one line, skipping the blanks so a missing value
// never leaves a stray separator or an empty " · · " gap.
const line = (...parts) => {
  const present = parts.filter((part) => part !== null && part !== undefined && part !== "");
  return present.length ? present.join(" · ") : null;
};

const personName = (person) => person?.name || null;

/** "Overdue by 3 days" in red, or the plain due date. */
const dueText = (value, isOverdue) => {
  const formatted = dateOnly(value);
  if (!formatted) return null;
  if (!isOverdue) return `Due ${formatted}`;
  return `Due ${formatted} (${dayjs.utc(value).fromNow()})`;
};

// A Board filing and its Compensation copy are the same file, so each row
// says where it went or the pair reads as a double send.
export const vaRow = (record) => ({
  key: record.id,
  name: record.file_name || record.filename || DASH,
  lines: [
    line(
      record.sent_by ? `Sent by ${record.sent_by}` : null,
      dateAndTime(record.sent_on),
    ),
    line(
      record.filed_with_label ? `Filed with ${record.filed_with_label}` : null,
      record.copy_note,
    ),
  ],
  status: renderVeteransAffairsStatus(record.status),
});

// Assignments and tasks are the same InboxTask rows; only the deliverable
// differs, so they share everything but that.
const workRow = (record, { includeDeliverable }) => ({
  key: record.id,
  name: record.document_name || record.body || DASH,
  lines: [
    line(
      personName(record.assigned_by) && personName(record.assigned_to)
        ? `${personName(record.assigned_by)} \u2192 ${personName(record.assigned_to)}`
        : personName(record.assigned_to) || personName(record.assigned_by),
      dueText(record.due_date, record.is_overdue),
    ),
    record.document_name ? record.body || null : null,
    includeDeliverable && record.deliverable_document_name
      ? `Submitted ${record.deliverable_document_name}`
      : null,
  ],
  status: renderStatus("assignment", record.status),
});

export const assignmentRow = (record) =>
  workRow(record, { includeDeliverable: true });

export const taskRow = (record) =>
  workRow(record, { includeDeliverable: true });

export const reviewRow = (record, { onBellClick } = {}) => ({
  key: record.id,
  name: record.file_name || record.filename || DASH,
  lines: [
    line(
      record.assigned_to || null,
      // A review is overdue on the same terms the Home table uses: only
      // while it is still outstanding. A due date on an approved review
      // is history, not a deadline.
      dueText(
        record.due_on,
        ["PENDING", "REWORK"].includes(record.status) &&
          record.due_on &&
          dayjs.utc(record.due_on).isBefore(dayjs.utc()),
      ),
    ),
    record.notes || null,
  ],
  status: renderSharedStatus(record),
  actions: onBellClick ? (
    <ReminderButton
      record={record}
      handleBellClick={onBellClick}
      canRemindField="reviewers"
      disabledStatuses={["APPROVED", "REWORK"]}
    />
  ) : null,
});

const participantNames = (participants) => {
  const names = (participants || [])
    .map((user) => user?.name || user?.full_name || user?.username)
    .filter(Boolean);
  return names.length ? names.join(", ") : "Unknown teammate";
};

export const messageRow = (record) => ({
  key: record.id,
  name: record.document_name || "Matter message",
  lines: [
    line(
      participantNames(record.participants),
      dateAndTime(record.last_message_at),
    ),
    record.latest_message?.body || null,
  ],
  status: record.unread_count ? `${record.unread_count} unread` : null,
});
